import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Truck, Download, ChevronDown, Search, Archive, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import api from '../../services/api';
import DetailPanel from '../SlidingPanel/DetailPanel';
import ShippingDetails from './ShippingDetails';
import styles from './ShippingPage.module.css';
import commonStyles from '../../styles/common.module.css';
import { toast } from 'react-toastify';
import { shouldSendToTelegram } from '../../utils/telegram';

const DropdownPortal = ({ children, parentRef }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (parentRef.current) {
      const rect = parentRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    }
  }, [parentRef]);

  return ReactDOM.createPortal(
    <div style={{ position: 'absolute', top: position.top, left: position.left, zIndex: 9999 }}>
      {children}
    </div>,
    document.body
  );
};

const ReportDropdown = ({ batchNumber, reportTemplates, handleDownloadReport, customerId }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  const handleTemplateClick = (template, e) => {
    e.stopPropagation();
    setOpen(false);
    handleDownloadReport(template, batchNumber);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const applicableTemplates = reportTemplates.filter(template => {
    return template.customer === null || (template.customer && template.customer.id === customerId);
  });

  return (
    <div className={styles.dropdownContainer}>
      <button ref={triggerRef} className={styles.dropdownTrigger} onClick={toggleDropdown}>
        <Download size={16} />
        <ChevronDown size={14} className={styles.chevron} />
      </button>
      {open && (
        <DropdownPortal parentRef={triggerRef}>
          <div ref={menuRef} className={styles.dropdownMenu}>
            {applicableTemplates.length > 0 ? (
              applicableTemplates.map((template) => (
                <button
                  key={template.id}
                  className={styles.dropdownItem}
                  onClick={(e) => handleTemplateClick(template, e)}
                >
                  <Download size={14} className={styles.dropdownItemIcon} />
                  {template.name}
                </button>
              ))
            ) : (
              <div className={styles.dropdownItem}>Нет доступных шаблонов</div>
            )}
          </div>
        </DropdownPortal>
      )}
    </div>
  );
};

const ShippingPage = ({ allProjects = false, embedded = false, hideModeToggle = false } = {}) => {
  const [shippingData, setShippingData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [reportTemplates, setReportTemplates] = useState([]);
  const [viewMode, setViewMode] = useState('awaiting');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const navigate = useNavigate();
  const [selectedBatchNumber, setSelectedBatchNumber] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const serverSearchTerm = allProjects ? '' : searchTerm;

  useEffect(() => {
    if (allProjects) {
      setReportTemplates([]);
    } else {
      fetchReportTemplates();
    }
    setPage(1);
    setShippingData([]);
    setHasMore(true);
    setTotalCount(0);
    setInitialLoading(!embedded);
    fetchShippingBoxes(true);
    console.log('ShippingPage: Текущий viewMode:', viewMode);
  }, [viewMode, allProjects, embedded, serverSearchTerm]);

  const getBoxProjectId = (box) => {
    if (box.project) {
      return typeof box.project === 'object' ? box.project.id : box.project;
    }
    if (box.repair_request?.project) {
      return typeof box.repair_request.project === 'object'
        ? box.repair_request.project.id
        : box.repair_request.project;
    }
    if (box.packaged_equipment?.length) {
      for (const eq of box.packaged_equipment) {
        if (eq.repair_request?.project) {
          return typeof eq.repair_request.project === 'object'
            ? eq.repair_request.project.id
            : eq.repair_request.project;
        }
      }
    }
    return null;
  };

  const getBoxProjectName = (box) => {
    if (box.project && typeof box.project === 'object') {
      return box.project.name || 'Не указан';
    }
    if (box.repair_request?.project && typeof box.repair_request.project === 'object') {
      return box.repair_request.project.name || 'Не указан';
    }
    if (box.packaged_equipment?.length) {
      for (const eq of box.packaged_equipment) {
        if (eq.repair_request?.project && typeof eq.repair_request.project === 'object') {
          return eq.repair_request.project.name || 'Не указан';
        }
      }
    }
    return 'Не указан';
  };

  const fetchShippingBoxes = async (reset = false) => {
    if (isFetching) {
      console.log('ShippingPage: Skipped fetchShippingBoxes, isFetching=true');
      return;
    }
    setIsFetching(true);
    const currentPage = reset ? 1 : page;
    console.log(`ShippingPage: Fetching shipping boxes, page: ${currentPage}, reset: ${reset}, search: ${searchTerm}`);

    try {
      const selectedProjectId = allProjects ? null : localStorage.getItem('selected_project_id');
      const numericSelectedProjectId = selectedProjectId ? Number(selectedProjectId) : null;
      const params = {
        page: currentPage,
        page_size: allProjects ? 100 : 50,
        view_mode: viewMode,
      };
      if (!allProjects && searchTerm) {
        params.search = searchTerm;
      }
      if (selectedProjectId) {
        params.project_id = selectedProjectId;
      }
      const resp = await api.get('/repairs/boxes/', {
        params,
      });

      console.log('ShippingPage: API Response:', {
        count: resp.data.count,
        next: resp.data.next,
        resultsLength: resp.data.results.length,
        results: resp.data.results,
      });

      if (!resp.data || !Array.isArray(resp.data.results)) {
        throw new Error('Некорректный формат ответа от сервера');
      }

      const { results, next, count } = resp.data;
      const projectFiltered = results.filter((box) => {
        if (allProjects || !numericSelectedProjectId) return true;
        const boxProjectId = getBoxProjectId(box);
        if (boxProjectId === null || boxProjectId === undefined) return true;
        const numericBoxProjectId = Number(boxProjectId);
        if (Number.isNaN(numericBoxProjectId)) return true;
        return numericBoxProjectId === numericSelectedProjectId;
      });
      // Минимальная фильтрация, так как сервер возвращает коробки с awaiting_shipping
      const candidateBoxes = projectFiltered.filter(box => 
        box.batch_number && 
        !box.is_open && 
        !box.shipping
      );

      console.log('ShippingPage: Filtered candidateBoxes:', candidateBoxes);

      const groups = reset ? {} : { ...shippingData.reduce((acc, group) => {
        const key = `${group.projectName}|${group.batchNumber}|${group.territoryCity}|${group.service_center}|${group.customer}|${group.customerCity}`;
        acc[key] = group;
        return acc;
      }, {}) };

      candidateBoxes.forEach((box) => {
        const projectName = getBoxProjectName(box);
        const batchNumber = box.batch_number || 'Без номера';
        const territoryCity = box.territory_city || 'Не указан';
        const serviceCenter = box.service_center_name || 'Не указан';
        const customerName = box.customer?.name || 'Не указан';
        const customerId = box.customer?.id || null;
        const customerCity = box.city?.name || 'Не указан';
        const key = `${projectName}|${batchNumber}|${territoryCity}|${serviceCenter}|${customerName}|${customerCity}`;
        if (groups[key]) {
          groups[key].count += 1;
        } else {
          groups[key] = {
            projectName,
            batchNumber,
            territoryCity,
            service_center: serviceCenter,
            customer: customerName,
            customerId,
            customerCity,
            count: 1,
          };
        }
      });

      const newData = Object.values(groups);
      setShippingData(newData);
      setTotalCount(count);
      setHasMore(!!next && newData.length < count);
      setPage(currentPage + 1);

      console.log('ShippingPage: State after fetch:', {
        newDataLength: newData.length,
        count,
        hasMore: !!next && newData.length < count,
        page: currentPage + 1,
        groups,
      });

      if (reset) setInitialLoading(false);
    } catch (err) {
      console.error('ShippingPage: Ошибка при загрузке данных:', err);
      setError('Ошибка при загрузке данных для отправки');
      setHasMore(false);
    } finally {
      setIsFetching(false);
    }
  };

  const fetchReportTemplates = async () => {
    try {
      const selectedProjectId = localStorage.getItem('selected_project_id');
      const selectedCustomerId = localStorage.getItem('selected_customer_id');
      const resp = await api.get('/reports/templates/', {
        params: {
          customer_id: selectedCustomerId,
          project_id: selectedProjectId,
        },
      });
      if (resp.data && resp.data.length > 0) {
        const filteredTemplates = resp.data.filter(template => template.target_page === 'shipping');
        setReportTemplates(filteredTemplates);
      } else {
        setReportTemplates([]);
      }
    } catch (err) {
      console.error('ShippingPage: Ошибка при загрузке шаблонов:', err);
      setError('Ошибка при загрузке шаблонов отчётов');
    }
  };

  const handleRowClick = (group) => {
    setSelectedBatchNumber(group.batchNumber);
    setShowDetailPanel(true);
  };

  const handleClosePanel = () => {
    setShowDetailPanel(false);
    setSelectedBatchNumber(null);
  };

  const handleDownloadReport = async (template, batchNumber) => {
    if (!template) {
      setError('Шаблон отчёта не найден');
      return;
    }
    try {
      const filename = `report_${batchNumber}_${new Date().toISOString().slice(0,10)}.xlsx`;
      const params = {
        template_id: template.id,
        batch_number: batchNumber,
      };

      if (shouldSendToTelegram()) {
        try {
          await api.get('/reports/generate/', { params: { ...params, send_to_telegram: true } });
          toast.success('Отчёт отправлен в Telegram', { autoClose: 2000 });
          return;
        } catch (err) {
          console.warn('Telegram send failed, fallback to download:', err);
        }
      }

      const response = await api.get('/reports/generate/', { params, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('ShippingPage: Ошибка при скачивании отчёта:', err);
      setError(err.response?.data?.error || 'Ошибка при скачивании отчёта');
    }
  };

  const handleDownloadAllReports = async () => {
    if (reportTemplates.length === 0) {
      setError('Нет доступных шаблонов отчётов для скачивания');
      return;
    }

    try {
      const batchData = filteredData.map(group => ({
        batch_number: group.batchNumber,
        customer_id: group.customerId,
        customer_name: group.customer,
      }));
      const templateIds = reportTemplates.map(template => template.id);
      const payload = { batch_data: batchData, template_ids: templateIds };

      if (shouldSendToTelegram()) {
        try {
          await api.post(
            '/reports/generate_all/',
            { ...payload, send_to_telegram: true }
          );
          toast.success('Архив отчётов отправлен в Telegram', { autoClose: 3000 });
          return;
        } catch (err) {
          console.warn('Telegram send failed, fallback to download:', err);
        }
      }

      const response = await api.post(
        '/reports/generate_all/',
        payload,
        { responseType: 'blob' }
      );

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `reports_${new Date().toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('ShippingPage: Ошибка при скачивании всех отчётов:', err);
      setError(err.response?.data?.error || 'Ошибка при скачивании всех отчётов');
    }
  };

  const filteredData = shippingData.filter((group) => {
    const term = searchTerm.toLowerCase();
    return (
      (group.projectName || '').toLowerCase().includes(term) ||
      group.batchNumber.toLowerCase().includes(term) ||
      group.territoryCity.toLowerCase().includes(term) ||
      group.service_center.toLowerCase().includes(term) ||
      group.customer.toLowerCase().includes(term) ||
      group.customerCity.toLowerCase().includes(term)
    );
  });

  if (error) return <div className={commonStyles.error}>{error}</div>;

  return (
    <div className={`${commonStyles.pageContainer} ${commonStyles.fadeIn}`}>
      <div className={commonStyles.pageHeader}>
        <div className={styles.titleGroup}>
          {viewMode === 'awaiting' ? (
            <Package className={styles.titleIcon} />
          ) : (
            <Truck className={styles.titleIcon} />
          )}
          <h1 className={commonStyles.pageTitle}>
            {allProjects ? 'Отправка по всем проектам' : 'Отправка оборудования'}
          </h1>
        </div>
        <div className={styles.filterContainer}>
          {!hideModeToggle && (
            <div className={styles.toggleContainer}>
              <button
                className={`${styles.toggleButton} ${viewMode === 'awaiting' ? styles.active : ''}`}
                onClick={() => {
                  setViewMode('awaiting');
                  if (!embedded) navigate('/shipping');
                }}
                title="Готово к отправке"
              >
                <Package className={styles.toggleIcon} />
              </button>
              <button
                className={`${styles.toggleButton} ${viewMode === 'shipped' ? styles.active : ''}`}
                onClick={() => {
                  setViewMode('shipped');
                  if (!embedded) navigate('/shipping/shipped');
                }}
                title="Отправленные"
              >
                <Truck className={styles.toggleIcon} />
              </button>
            </div>
          )}
          <div className={commonStyles.searchContainer}>
            <Search className={commonStyles.searchIcon} />
            <input
              type="text"
              placeholder={allProjects ? 'Поиск по проекту, партии, территории, заказчику...' : 'Поиск по номеру партии, территории, заказчику...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={commonStyles.searchInput}
            />
          </div>
          {reportTemplates.length > 0 && (
            <button className={styles.downloadButton} onClick={handleDownloadAllReports}>
              <Archive size={16} style={{ marginRight: '8px' }} />
              Скачать все отчёты
            </button>
          )}
        </div>
      </div>
      {isFetching && shippingData.length === 0 && !initialLoading && (
        <div className={styles.inlineLoading}>Загрузка отправок...</div>
      )}
      {initialLoading && shippingData.length === 0 ? (
        <div className={commonStyles.loading}>
          <div className={commonStyles.spinner}></div>
        </div>
      ) : (
        <div id="scrollableDiv" className={styles.tableWrapper}>
          <InfiniteScroll
            key="shipping"
            dataLength={shippingData.length}
            next={() => fetchShippingBoxes()}
            hasMore={hasMore}
            loader={<div className={commonStyles.loadingMore}><div className={commonStyles.spinner}></div></div>}
            scrollableTarget="scrollableDiv"
            scrollThreshold={0.9}
          >
            <table className={commonStyles.table}>
              <thead>
                <tr>
                  {allProjects && <th>Проект</th>}
                  <th>Номер партии</th>
                  <th>Территория Город</th>
                  <th>Сервисный центр</th>
                  <th>Заказчик</th>
                  <th>Город заказчика</th>
                  <th>Кол-во коробок</th>
                  {reportTemplates.length > 0 && <th>Скачать отчёт</th>}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((group, index) => (
                  <tr
                    key={index}
                    onClick={() => handleRowClick(group)}
                    className={commonStyles.tableRow}
                  >
                    {allProjects && <td>{group.projectName}</td>}
                    <td>{group.batchNumber}</td>
                    <td>{group.territoryCity}</td>
                    <td>{group.service_center}</td>
                    <td>{group.customer}</td>
                    <td>{group.customerCity}</td>
                    <td>{group.count}</td>
                    {reportTemplates.length > 0 && (
                      <td onClick={(e) => e.stopPropagation()}>
                        <ReportDropdown
                          batchNumber={group.batchNumber}
                          reportTemplates={reportTemplates}
                          handleDownloadReport={handleDownloadReport}
                          customerId={group.customerId}
                        />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </InfiniteScroll>
        </div>
      )}

      {/* Detail Panel */}
      {showDetailPanel && selectedBatchNumber && (
        <DetailPanel
          isOpen={showDetailPanel}
          onClose={handleClosePanel}
          title="Детали отправки"
        >
          <ShippingDetails batchNumber={selectedBatchNumber} allProjects={allProjects} />
        </DetailPanel>
      )}
    </div>
  );
};

export default ShippingPage;
